import Color from "color";
import { Link } from "gatsby";
import styled from "styled-components";
import { DARK_GRAY, GREIGE, PAPER, WHITE, YELLOW } from "../../../theme/colors";
import { SIZE, ZINDEX_NAVBAR, mediaQuery, unit } from "../../../theme/metrics";
import { typeBodySm, typeLabelMd } from "../../../theme/typography";

export const StyledMenuContainer = styled.div`
  padding: 0 ${unit(3)};
  display: flex;
  position: relative;
  flex-direction: column;
  order: -1;
  flex: 1;
  border-top: 1px solid ${DARK_GRAY};
  overflow: auto;

  ${mediaQuery[SIZE.large]} {
    border-top: none;
    justify-content: center;
    align-items: center;
    flex-direction: row;
    padding: 0 ${unit(4)};
    order: initial;
    overflow: initial;
    flex: 0 0 ${unit(12)};
    height: ${unit(12)};
  }
`;

export const StyledMenuLinks = styled.ul<{ $isCompact?: boolean }>`
  display: flex;
  flex-direction: column;
  ${typeLabelMd}
  column-gap: ${unit(4)};
  padding: ${unit(2)} 0;

  a, span{
    text-decoration: none;
    white-space: nowrap;
    cursor: pointer;
    display: block;
    user-select: none;
  }

  >li{
    position: relative;

    >a, >span{
      padding: ${unit(2)} 0;
    }

    >span{
      position: relative;

      &:before, &:after{
        content: "";
        background-color: ${DARK_GRAY};
        width: 20px;
        height: 1px;
        position: absolute;
        right: 0;
        top: 50%;
      }

      &:after{
        transform: rotate(90deg);
      }
    }

    &[data-open="true"]{
      >span{
        &:after{
          display: none;
        }
      }

      >ul{
        display: flex;
      }
    }

    >ul{
      display: none;
      flex-direction: column;

      >li{
        >a{
          color: ${DARK_GRAY};
          padding: ${unit(2)} ${unit(3)};
        }
      }
    }
  }

  ${mediaQuery[SIZE.large]} {
    flex-direction: row;
    padding: 0;
    display: ${({ $isCompact }) => $isCompact ? `none` : `flex`};

    >li{

      >ul{
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        text-align: center;
        background-color: ${PAPER};
        padding: ${unit(1.5)} ${unit(3)};
        display: flex;
        flex-direction: column;
        visibility: hidden;
        opacity: 0;
        pointer-events: none;
        transition: visibility 0.3s, opacity 0.3s;

        >li{
          >a{
            color: ${DARK_GRAY};
            padding: ${unit(1.5)} 0;
            transition: color 0.5s;

            &:hover{
              color: ${Color(DARK_GRAY).alpha(0.7).string()};
            }
          }
        }
      }

      &[data-open="true"]{
        >ul{
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
        }
      }

      &[data-over="true"]{
        >ul{
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
        }
      }

      >span{
        &:before, &:after{
          content: none;
        }
      }

      >a, >span{
        &:after{
          position: absolute;
          content: '';
          left: 0;
          top: 50%;
          width: 100%;
          height: 1px;
          background-color: ${YELLOW};
          transition: transform 280ms ease-out;
          transform: translateY(10px) scaleX(0);
        }

        &:hover{
          &:after{
            transform: translateY(10px) scaleX(1);
          }
        }
      }
    }
  }
`

export const StyledLogo = styled(Link)`
  position: absolute;
  top: 26px;
  left: ${unit(3)};
  z-index: 1;

  svg{
    height: 30px;
  }

  ${mediaQuery[SIZE.large]} {
    left: ${unit(4)};
    top: 50%;
    transform: translateY(-50%);

    svg{
      height: 45px;
    }
  }
`;

export const StyledBurger = styled.button`
  width: ${unit(5)};
  height: ${unit(5)};
  position: absolute;
  right: ${unit(3)};
  top: ${unit(2.5)};
  z-index: 1;
  cursor: pointer;
  background: transparent;
  
  &:before, &:after{
    content: "";
    height: 1px;
    width: 100%;
    left: 0;
    position: absolute;
    top: 50%;
    transition: transform 0.5s;
  }

  ${mediaQuery[SIZE.large]} {
    display: none;
  }
`

export const StyledHeaderContent = styled.div`
  width: 100%;
  background-color: ${PAPER};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: ${unit(10)};
  position: relative;

  ${mediaQuery[SIZE.large]} {
    position: static;
    background-color: transparent;
    padding-top: 0;
  }
`

export const StyledHeaderContainer = styled.div<{ $sticky: boolean, $open: boolean }>`
  position: fixed;
  top: 0;
  z-index: ${ZINDEX_NAVBAR};
  left: 0;
  width: 100%;
  height: ${({ $open }) => $open ? `100%` : unit(10)};
  background-color: ${({ $sticky }) => $sticky ? DARK_GRAY : `transparent`};
  transition: background-color 0.5s;
  display: flex;

  ${mediaQuery[SIZE.large]} {
    position: fixed;
    top: 0;
    height: auto;
  }

  ${StyledHeaderContent}{
    visibility: ${({ $open }) => $open ? `visible` : `hidden`};

    ${mediaQuery[SIZE.large]} {
      visibility: visible;
    }
  }

  ${StyledMenuLinks}{
    >li{
      >a, >span{
        color: ${DARK_GRAY};
        transition: color 0.5s;
      }
    }
    ${mediaQuery[SIZE.large]} {
      >li{
        >a, >span{
          color: ${({ theme, $sticky }) => $sticky ? WHITE : theme.primary};
        }
      }
    }
  }

  ${StyledLogo}{
    svg{
      fill: ${({ theme, $sticky, $open }) => $open ? DARK_GRAY : $sticky ? WHITE : theme.primary};
      transition: fill 0.5s;
    }

    ${mediaQuery[SIZE.large]} {
      svg{
        fill: ${({ theme, $sticky }) => $sticky ? WHITE : theme.primary};
      }
    }
  }

  ${StyledBurger}{
    &:before, &:after{
      background-color: ${({ theme, $sticky, $open }) => $open ? DARK_GRAY : $sticky ? WHITE : theme.primary};
    }

    &:before{
      transform: ${({ $open }) => $open ? `rotate(-45deg)` : `translateY(-4px)`};
    }

    &:after{
      transform: ${({ $open }) => $open ? `rotate(45deg)` : `translateY(4px)`};
    }

    ${mediaQuery[SIZE.large]} {
      &:before, &:after{
        background-color: ${({ theme, $sticky }) => $sticky ? WHITE : theme.primary};
      }
    }
  }
`

export const StyledActionButton = styled.button`
  ${typeLabelMd}
  background-color: ${YELLOW};
  color: ${DARK_GRAY};
  border: none;
  text-align: center;
  cursor: pointer;
  padding-inline: ${unit(3)};
  display: none;

  ${mediaQuery[SIZE.large]} {
    display: block;
    height: ${unit(6)};
  }
`;

export const StyledActionLink = styled.a`
  ${typeLabelMd}
  color: ${WHITE};
  display: flex;
  column-gap: ${unit(1)};
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  padding-block: ${unit(2)};

  span:not(:first-child){
    font-weight: 300;
  }

  ${mediaQuery[SIZE.large]} {
    color: ${DARK_GRAY};
    padding-inline: ${unit(3)};
    padding-block: 0;
    height: ${unit(6)};
    flex-direction: column;
    justify-content: center;
    transition: background-color 0.3s;

    &:hover{
      background-color: ${PAPER};
    }
  }
`

export const StyledActionLinks = styled.ul`
  display: flex;
  flex-direction: column;

  li{
    &:last-child{
      ${StyledActionLink}{
        background-color: ${YELLOW};
        color: ${DARK_GRAY};
        justify-content: center;
        height: ${unit(6)};
        margin-top: ${unit(2)};
      }
    }
  }

  ${mediaQuery[SIZE.large]} {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    background-color: ${WHITE};
    transition: visibility 0.3s, opacity 0.3s;

    li{
      border-bottom: 1px solid ${Color(DARK_GRAY).alpha(0.3).string()};

      &:last-child{
        border-bottom: none;
        
        ${StyledActionLink}{
          margin-top: 0;
          background-color: transparent;

          &:hover{
            background-color: ${PAPER};
          }
        }
      }
    }
  }
`;

export const StyledLink = styled.a``

export const StyledActionContainer = styled.div`
  padding-top: ${unit(2)};
  padding-bottom: ${unit(4)};
  padding-inline: ${unit(3)};
  background-color: ${DARK_GRAY};
  display: flex;
  flex-direction: column;
  user-select: none;

  ${mediaQuery[SIZE.large]} {
    background-color: transparent;
    position: absolute;
    right: ${unit(4)};
    top: ${unit(3)};
    padding: 0;
    clip-path: rect(0 100% ${unit(6)} 0);
  }

  &[data-open="true"]{
    clip-path: none;
    ${StyledActionLinks}{
      visibility: visible;
      pointer-events: auto;
      opacity: 1;
    }
  }
`